import { Head, Link, usePage } from "@inertiajs/react"
import { MessageSquarePlus, PlayCircle, Sparkles } from "lucide-react"

import {
  type ReplayListing,
  ReplaySessionsCard,
} from "@/components/dashboard/replay-sessions-card"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import AppLayout from "@/layouts/app-layout"
import { adminReplayIndexPath, chatPath, dashboardPath } from "@/routes"
import type { BreadcrumbItem } from "@/types"

const breadcrumbs: BreadcrumbItem[] = [
  { title: "Dashboard", href: dashboardPath() },
]

interface DashboardProps {
  greeting_name: string
  has_previous_login: boolean
  new_conversations_count: number
  stats: {
    total_conversations: number
    total_messages: number
    completed_conversations: number
    npc_turns: number
  }
  replay: ReplayListing
  [key: string]: unknown
}

export default function Dashboard() {
  const {
    greeting_name,
    has_previous_login,
    new_conversations_count,
    stats,
    replay,
  } = usePage<DashboardProps>().props

  const sinceLabel = has_previous_login ? "since you last logged in" : "so far"
  const reviewLine =
    new_conversations_count > 0
      ? `There have been ${new_conversations_count.toLocaleString()} conversations ${sinceLabel}. Review below.`
      : `No new conversations ${has_previous_login ? "since you last logged in" : "yet"} — review past sessions below.`

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Dashboard" />

      <div className="flex h-full flex-1 flex-col gap-4 p-4">
        <div className="grid auto-rows-min gap-4 md:grid-cols-3">
          {/* #1 — no card: inline welcome message */}
          <div className="flex flex-col justify-center px-1">
            <h1 className="text-2xl font-semibold tracking-tight">
              Welcome back, {greeting_name}
            </h1>
            <p className="text-muted-foreground mt-1 text-sm text-balance">
              {reviewLine}
            </p>
          </div>

          {/* #2 — Practice activity */}
          <Card className="gap-2">
            <CardHeader>
              <CardTitle className="text-muted-foreground text-sm font-medium">
                Practice activity
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-semibold tabular-nums">
                {stats.total_conversations.toLocaleString()}
              </div>
              <p className="text-muted-foreground mt-1 text-sm">
                conversations
              </p>
              <p className="text-muted-foreground mt-3 text-xs">
                {stats.total_messages.toLocaleString()} messages ·{" "}
                {stats.completed_conversations.toLocaleString()} completed
              </p>
            </CardContent>
          </Card>

          {/* #3 — Quick actions */}
          <Card className="gap-2">
            <CardHeader>
              <CardTitle className="text-muted-foreground text-sm font-medium">
                Quick actions
              </CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-2">
              <Button asChild className="w-full justify-start">
                <a href={`${chatPath()}?script=demo_golden_path`}>
                  <PlayCircle className="size-4" />
                  Run live demo
                </a>
              </Button>
              <div className="flex gap-2">
                <Button asChild variant="outline" className="flex-1">
                  <Link href={chatPath()}>
                    <MessageSquarePlus className="size-4" />
                    New chat
                  </Link>
                </Button>
                <Button asChild variant="outline" className="flex-1">
                  <Link href={adminReplayIndexPath()}>Browse replays</Link>
                </Button>
              </div>
              <p className="text-muted-foreground mt-1 inline-flex items-center gap-1 text-xs">
                <Sparkles className="size-3" />
                {stats.npc_turns.toLocaleString()} NPC turns to date
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Row 2 — largest card: embedded replay listing */}
        <ReplaySessionsCard replay={replay} className="flex-1" />
      </div>
    </AppLayout>
  )
}
